/**
 * Hero — the top section of the Briefing tab.
 *
 * Two-column card: verdict (headline + body + CTA) on the left, two contrast
 * meters on the right. The meters are the two elements the Briefing AI's
 * headline contrasts (resolved to their real BE scores from `data.signals`);
 * when the AI doesn't supply a `contrast`, they fall back to the deterministic
 * love-group ("Strength among knowers") / know-group ("Visibility") averages.
 */

import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { LockOutlined } from '@ant-design/icons';
import type { MarketerViewData, ScoreNode } from '../types';
import type { BriefingNarrative } from '../useBriefingData';
import { BAND_PALETTE } from '../config/bandColours';
import { colourForScore } from '../config/elementColours';
import { THEME } from '../config/theme';
import { useScopedNavigate } from '../useScopedNavigate';
import { MARKETER_UPGRADE_ROUTE } from '../gating/featureMatrix';
import { Card } from '../atoms';
import { Eyebrow } from '../atoms';
import { Skeleton, SkeletonLines } from '../atoms';
import { ContrastMeter, type ContrastMeterProps } from './ContrastMeter';

export interface HeroProps {
  data: MarketerViewData;
  /** Briefing AI narrative — drives the contrast meters; omit to use the
   *  deterministic love/know averages (legacy BriefingTab). */
  narrative?: BriefingNarrative | null;
  /** True while the AI verdict is still loading — show skeletons, not the
   *  deterministic placeholder (which reads as a finished, low-confidence take). */
  narrativeLoading?: boolean;
  /** True while per-element AI summaries load — skeletons the meter captions. */
  aiLoading?: boolean;
  /** Primary CTA — jumps straight to the Recommendations. */
  onRecommendations: () => void;
  /** Open the element drawer for a meter's element (title/bar/value click).
   *  Only wired in the AI path where each meter maps to a real ScoreNode. */
  onOpen?: (node: ScoreNode) => void;
  /** Tier-locked: blur the verdict + meters (the headline is synthesised from
   *  locked elements), keep the eyebrow clear, swap the CTA to upgrade. */
  locked?: boolean;
}

const BLUR_STYLE: React.CSSProperties = {
  filter: 'blur(7px)',
  opacity: 0.4,
  pointerEvents: 'none',
  userSelect: 'none',
};

/** A loading placeholder for one contrast meter (label + bar + caption). */
const SkeletonMeter: React.FunctionComponent = () => (
  <div>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 16,
        marginBottom: 10,
      }}
    >
      <Skeleton width={150} height={14} />
      <Skeleton width={56} height={26} radius={6} />
    </div>
    <Skeleton height={6} radius={3} />
    <SkeletonLines lines={2} height={11} gap={6} lastWidth="80%" style={{ marginTop: 11 }} />
  </div>
);

export const Hero: React.FunctionComponent<HeroProps> = ({
  data,
  narrative,
  narrativeLoading,
  aiLoading,
  onRecommendations,
  onOpen,
  locked = false,
}) => {
  const navigate = useScopedNavigate();
  const byCanonical = useMemo(
    () => new Map(data.signals.map((s) => [s.canonical, s])),
    [data.signals],
  );
  const contrast = narrative?.one_thing?.contrast;
  const resolve = (name?: string): ScoreNode | undefined =>
    name
      ? byCanonical.get(name) ?? data.signals.find((s) => s.display === name)
      : undefined;
  const leftNode = resolve(contrast?.left?.element);
  const rightNode = resolve(contrast?.right?.element);
  const useAi = leftNode?.value != null && rightNode?.value != null;

  // The AI verdict (headline/body/contrast) is still resolving — skeleton it
  // rather than show the deterministic placeholder, which reads as a finished
  // take. Once the narrative lands (or is genuinely absent) we render normally.
  const verdictLoading = !!narrativeLoading && !narrative?.one_thing;

  // Two AI-picked elements (real scores), else the love/know group averages.
  // In AI mode the caption is the element's AI summary — skeleton it until the
  // (slower) per-element summary pass lands, so we don't flash the short note.
  const leftMeter: ContrastMeterProps = useAi
    ? {
        label: leftNode!.display,
        value: leftNode!.value as number,
        tone: colourForScore(leftNode!.canonical, leftNode!.value),
        caption: leftNode!.aiSummary || leftNode!.note,
        loading: !!aiLoading && !leftNode!.aiSummary,
        onOpen: onOpen ? () => onOpen(leftNode!) : undefined,
      }
    : {
        labelId: data.hero.strength.labelId,
        defaultLabel: 'Strength among people who know you',
        value: data.hero.strength.value,
        tone: BAND_PALETTE.strong.fg,
        caption: data.hero.strength.caption,
      };
  const rightMeter: ContrastMeterProps = useAi
    ? {
        label: rightNode!.display,
        value: rightNode!.value as number,
        tone: colourForScore(rightNode!.canonical, rightNode!.value),
        caption: rightNode!.aiSummary || rightNode!.note,
        loading: !!aiLoading && !rightNode!.aiSummary,
        onOpen: onOpen ? () => onOpen(rightNode!) : undefined,
      }
    : {
        labelId: data.hero.visibility.labelId,
        defaultLabel: 'Visibility in the open market',
        value: data.hero.visibility.value,
        tone: BAND_PALETTE.weak.fg,
        caption: data.hero.visibility.caption,
      };

  return (
    <section id="aha" style={{ paddingTop: 30 }}>
      <Card pad={0} style={{ overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.15fr 0.85fr' }}>
          {/* Left: verdict */}
          <div style={{ padding: '44px 46px 46px' }}>
            <Eyebrow color={BAND_PALETTE.weak.fg}>
              <FormattedMessage
                id="marketerView.hero.eyebrow"
                defaultMessage="If you only know one thing"
              />
            </Eyebrow>
            {locked ? (
              <div style={BLUR_STYLE} aria-hidden>
                <h1
                  style={{
                    fontSize: 38,
                    lineHeight: 1.08,
                    fontWeight: 600,
                    color: '#0F172A',
                    letterSpacing: '-0.025em',
                    margin: '16px 0 0',
                  }}
                >
                  {data.hero.headline}
                </h1>
                <p
                  style={{
                    fontSize: 16,
                    lineHeight: 1.62,
                    color: '#64748B',
                    margin: '20px 0 0',
                  }}
                >
                  {data.hero.body}
                </p>
              </div>
            ) : verdictLoading ? (
              <>
                <div style={{ margin: '20px 0 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <Skeleton height={30} width="95%" radius={8} />
                  <Skeleton height={30} width="60%" radius={8} />
                </div>
                <SkeletonLines lines={4} height={13} gap={10} lastWidth="55%" style={{ marginTop: 24 }} />
              </>
            ) : (
              <>
                <h1
                  style={{
                    fontSize: 38,
                    lineHeight: 1.08,
                    fontWeight: 600,
                    color: '#0F172A',
                    letterSpacing: '-0.025em',
                    margin: '16px 0 0',
                  }}
                >
                  {data.hero.headline}
                </h1>
                <p
                  style={{
                    fontSize: 16,
                    lineHeight: 1.62,
                    color: '#64748B',
                    margin: '20px 0 0',
                  }}
                >
                  {data.hero.body}
                </p>
              </>
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 10,
                marginTop: 28,
                flexWrap: 'wrap',
              }}
            >
              <button
                type="button"
                onClick={
                  locked
                    ? () => navigate(MARKETER_UPGRADE_ROUTE)
                    : onRecommendations
                }
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  border: 'none',
                  background: THEME.primary,
                  color: '#fff',
                  fontSize: 14,
                  fontWeight: 700,
                  padding: '11px 18px',
                  borderRadius: 12,
                  cursor: 'pointer',
                }}
              >
                {locked ? (
                  <>
                    <LockOutlined />
                    <FormattedMessage
                      id="marketerView.hero.cta.upgrade"
                      defaultMessage="Upgrade to unlock"
                    />
                  </>
                ) : (
                  <FormattedMessage
                    id="marketerView.hero.cta.recommendations"
                    defaultMessage="Jump straight to the Recommendations"
                  />
                )}
              </button>
            </div>
          </div>
          {/* Right: contrast meters on a light tinted surface */}
          <div
            style={{
              background: THEME.bgTinted,
              borderLeft: `1px solid ${THEME.border}`,
              padding: '40px 40px 44px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 26,
            }}
          >
            {locked ? (
              <div style={BLUR_STYLE} aria-hidden>
                <ContrastMeter {...leftMeter} />
                <div style={{ height: 1, background: THEME.borderSubtle, margin: '26px 0' }} />
                <ContrastMeter {...rightMeter} />
              </div>
            ) : verdictLoading ? (
              <>
                <SkeletonMeter />
                <div style={{ height: 1, background: THEME.borderSubtle }} />
                <SkeletonMeter />
              </>
            ) : (
              <>
                <ContrastMeter {...leftMeter} />
                <div style={{ height: 1, background: THEME.borderSubtle }} />
                <ContrastMeter {...rightMeter} />
              </>
            )}
          </div>
        </div>
      </Card>
    </section>
  );
};
